export const AUTHORED_CIVIC_SQUARE_COLLISION_SCHEMA_VERSION = 2 as const;

export interface AuthoredCivicSquareCollisionSpec {
  readonly id: string;
  readonly tileId: string;
  readonly position: Readonly<{
    x: number;
    y: number;
    z: number;
  }>;
  readonly halfExtentsM: Readonly<{
    x: number;
    y: number;
    z: number;
  }>;
  readonly rotationYRadians: number;
  readonly classification: "building";
  readonly sourceFeatureIds: readonly string[];
  readonly enabled: true;
}

export interface AuthoredCivicSquareCollisionManifest {
  readonly schemaVersion:
    typeof AUTHORED_CIVIC_SQUARE_COLLISION_SCHEMA_VERSION;
  readonly coordinateBasis: "local-projected-world-metres";
  readonly collisionPrimitives: readonly AuthoredCivicSquareCollisionSpec[];
}

export function parseAuthoredCivicSquareCollisionManifest(
  value: unknown,
): AuthoredCivicSquareCollisionManifest {
  const manifest = requireRecord(value, "Civic-square collision manifest");
  if (
    manifest.schemaVersion !==
    AUTHORED_CIVIC_SQUARE_COLLISION_SCHEMA_VERSION
  ) {
    throw new Error(
      "Civic-square collision manifest schemaVersion must equal 2.",
    );
  }
  if (manifest.coordinateBasis !== "local-projected-world-metres") {
    throw new Error(
      "Civic-square collision manifest must use local-projected-world-metres.",
    );
  }
  if (!Array.isArray(manifest.collisionPrimitives)) {
    throw new Error(
      "Civic-square collision manifest collisionPrimitives must be an array.",
    );
  }

  const ids = new Set<string>();
  const collisionPrimitives = manifest.collisionPrimitives.map(
    (rawPrimitive, primitiveIndex) => {
      const path = `collisionPrimitives[${primitiveIndex}]`;
      const primitive = requireRecord(rawPrimitive, path);
      const id = requireString(primitive.id, `${path}.id`);
      if (ids.has(id)) {
        throw new Error(`Duplicate civic-square collision ID ${id}.`);
      }
      ids.add(id);
      const position = requireRecord(primitive.position, `${path}.position`);
      const halfExtentsM = requireRecord(
        primitive.halfExtentsM,
        `${path}.halfExtentsM`,
      );
      if (primitive.classification !== "building") {
        throw new Error(`${path}.classification must equal building.`);
      }
      if (primitive.enabled !== true) {
        throw new Error(`${path}.enabled must equal true.`);
      }
      if (!Array.isArray(primitive.sourceFeatureIds)) {
        throw new Error(`${path}.sourceFeatureIds must be an array.`);
      }

      return {
        id,
        tileId: requireTileId(primitive.tileId, `${path}.tileId`),
        position: {
          x: requireFiniteNumber(position.x, `${path}.position.x`),
          y: requireFiniteNumber(position.y, `${path}.position.y`),
          z: requireFiniteNumber(position.z, `${path}.position.z`),
        },
        halfExtentsM: {
          x: requirePositiveNumber(
            halfExtentsM.x,
            `${path}.halfExtentsM.x`,
          ),
          y: requirePositiveNumber(
            halfExtentsM.y,
            `${path}.halfExtentsM.y`,
          ),
          z: requirePositiveNumber(
            halfExtentsM.z,
            `${path}.halfExtentsM.z`,
          ),
        },
        rotationYRadians: requireFiniteNumber(
          primitive.rotationYRadians,
          `${path}.rotationYRadians`,
        ),
        classification: "building" as const,
        sourceFeatureIds: primitive.sourceFeatureIds.map(
          (sourceFeatureId, sourceFeatureIndex) =>
            requireString(
              sourceFeatureId,
              `${path}.sourceFeatureIds[${sourceFeatureIndex}]`,
            ),
        ),
        enabled: true as const,
      };
    },
  );

  return {
    schemaVersion: AUTHORED_CIVIC_SQUARE_COLLISION_SCHEMA_VERSION,
    coordinateBasis: "local-projected-world-metres",
    collisionPrimitives,
  };
}

export async function loadAuthoredCivicSquareCollisions(
  manifestUrl: string,
  fetcher: typeof fetch = fetch,
): Promise<readonly AuthoredCivicSquareCollisionSpec[]> {
  const response = await fetcher(manifestUrl);
  if (!response.ok) {
    throw new Error(
      `Could not load civic-square collision manifest (${response.status}).`,
    );
  }
  return parseAuthoredCivicSquareCollisionManifest(await response.json())
    .collisionPrimitives;
}

function requireRecord(
  value: unknown,
  path: string,
): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object.`);
  }
  return value as Record<string, unknown>;
}

function requireString(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(`${path} must be a non-empty string.`);
  }
  return value;
}

function requireTileId(value: unknown, path: string): string {
  const tileId = requireString(value, path);
  if (!/^-?\d+:-?\d+$/.test(tileId)) {
    throw new Error(`${path} must be a world tile ID.`);
  }
  return tileId;
}

function requireFiniteNumber(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${path} must be finite.`);
  }
  return value;
}

function requirePositiveNumber(value: unknown, path: string): number {
  const result = requireFiniteNumber(value, path);
  if (result <= 0) {
    throw new Error(`${path} must be positive.`);
  }
  return result;
}
